import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const esc = (t) => String(t ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

function openPrint(title, css, body) {
    const w = window.open("", "_blank", "width=760,height=960");
    if (!w) return;
    w.document.write(`<!DOCTYPE html><html><head><title>${esc(title)}</title><style>body{margin:0;font-family:Georgia,serif;color:#1a1206}${css}</style></head><body>${body}<script>window.onload=()=>window.print()</script></body></html>`);
    w.document.close();
}

const noteCardHtml = (o) => {
    const golden = o.tier === "Golden Note";
    return `<div class="card" style="border:2px solid ${golden ? "#c9a84c" : "#a7a9ac"};padding:0.5in;min-height:5.6in;display:flex;flex-direction:column;justify-content:space-between;page-break-inside:avoid">
        <div style="letter-spacing:.35em;text-transform:uppercase;font-size:11px;color:${golden ? "#9a7a24" : "#6d6f72"}">${esc(o.tier)} · Island Gold</div>
        <div style="font-size:${(o.note || "").length > 200 ? 17 : 22}px;line-height:1.55;font-style:italic;white-space:pre-wrap;margin:0.4in 0">“${esc(o.note)}”</div>
        <div style="font-size:11px;letter-spacing:.25em;text-transform:uppercase;color:#6b5a3a">Handmade in Hawaiʻi · Island Gold</div></div>`;
};

function printCard(o) {
    openPrint(o.tier, "@page{size:5in 7in;margin:0.4in}", noteCardHtml(o));
}

function printSlip(o) {
    const rows = o.items.map((i) => `<tr><td style="padding:6px 0">${esc(i.name)}</td><td style="text-align:right">× ${i.qty}</td><td style="width:30px;text-align:right">☐</td></tr>`).join("");
    const body = `<div style="padding:0.5in">
        <div style="letter-spacing:.35em;text-transform:uppercase;font-size:11px;color:#9a7a24">Island Gold · Packing slip</div>
        <h1 style="font-size:26px;margin:8px 0 4px">Order ${esc(o.id.slice(0, 8))}</h1>
        <p style="font-size:13px;color:#6b5a3a;margin:0">${esc(new Date(o.created_at).toLocaleString(undefined, { timeZoneName: "short" }))}</p>
        <table style="width:100%;margin-top:20px;border-collapse:collapse;font-size:15px">${rows}
            ${o.wrap ? '<tr><td style="padding:6px 0;font-weight:bold">Banana-leaf gift wrap</td><td></td><td style="text-align:right">☐</td></tr>' : ""}
            ${o.note ? `<tr><td style="padding:6px 0;font-weight:bold">${esc(o.tier)} card (below)</td><td></td><td style="text-align:right">☐</td></tr>` : ""}
        </table>
        <p style="margin-top:24px;font-size:13px;color:#6b5a3a">Ship-to address: see the order in Stripe.</p></div>
        ${o.note ? `<div style="page-break-before:always;padding:0.4in;width:5in">${noteCardHtml(o)}</div>` : ""}`;
    openPrint(`Packing slip ${o.id.slice(0, 8)}`, "@page{size:letter;margin:0.3in}", body);
}

function LeaveQuestionEditor() {
    const [q, setQ] = useState(null);
    useEffect(() => {
        api.get("/public/insiders/unsub-question").then(({ data }) => setQ({ question: data.question, choices: data.choices.join("\n") })).catch(() => {});
    }, []);
    if (!q) return null;
    const save = async () => {
        try {
            const { data } = await api.put("/partner/insiders/unsub-question", { question: q.question, choices: q.choices.split("\n") });
            setQ({ question: data.question, choices: data.choices.join("\n") });
            toast.success("Leaving question saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        }
    };
    const field = "w-full bg-transparent border border-cream/30 rounded-sm px-3 py-2 text-sm text-cream";
    return (
        <div className="mt-6 pt-4 border-t border-cream/10" data-testid="leave-question-editor">
            <p className="text-[0.62rem] tracking-[0.3em] uppercase text-gold">Site text · Insiders leaving question</p>
            <input className={`${field} mt-2`} value={q.question} onChange={(e) => setQ({ ...q, question: e.target.value })} data-testid="leave-question-input" />
            <p className="mt-2 text-xs">Choices (one per line; a choice named "Other" shows a text box)</p>
            <textarea rows={4} className={`${field} mt-1`} value={q.choices} onChange={(e) => setQ({ ...q, choices: e.target.value })} data-testid="leave-choices-input" />
            <button onClick={save} className="mt-2 btn-pill btn-ghost text-[0.6rem]" data-testid="leave-question-save">Save</button>
        </div>
    );
}

export function GiftNotesCard() {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get("/partner/note-sales").then(({ data }) => setData(data)).catch(() => setData(false));
    }, []);
    if (!data) return null;
    return (
        <div className="mb-8 p-5 border border-cream/15 rounded-sm text-sm text-cream" data-testid="gift-notes-card">
            <p className="text-[0.62rem] tracking-[0.3em] uppercase text-gold">Gift notes &amp; wrap</p>
            {data.months.length === 0 ? (
                <p className="mt-2">No gift notes or wraps sold yet.</p>
            ) : (
                <table className="mt-3 w-full text-left" data-testid="note-sales-table">
                    <thead><tr><th className="py-1">Month</th><th>Silver</th><th>Golden</th><th>Notes $</th><th>Wraps</th><th>Wrap $</th><th>Total</th></tr></thead>
                    <tbody>
                        {data.months.map((m) => (
                            <tr key={m.month} data-testid={`note-sales-${m.month}`}>
                                <td className="py-1">{m.month}</td><td>{m.silver}</td><td>{m.golden}</td><td>${m.note_revenue.toFixed(2)}</td>
                                <td>{m.wraps}</td><td>${m.wrap_revenue.toFixed(2)}</td><td>${m.revenue.toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            {data.orders.length > 0 && (
                <ul className="mt-4 space-y-2">
                    {data.orders.map((o) => (
                        <li key={o.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-cream/10 pt-2" data-testid={`note-order-${o.id}`}>
                            <span className="min-w-0 truncate">
                                Order {o.id.slice(0, 8)} · {o.note ? `${o.tier} “${o.note.slice(0, 50)}${o.note.length > 50 ? "…" : ""}”` : "No note"}{o.wrap ? " · Gift wrap" : ""}
                            </span>
                            <span className="flex gap-2">
                                {o.note && (
                                    <button onClick={() => printCard(o)} className="btn-pill btn-ghost text-[0.6rem] inline-flex items-center gap-1" data-testid={`note-print-${o.id}`}>
                                        <Printer size={12} /> Print card
                                    </button>
                                )}
                                <button onClick={() => printSlip(o)} className="btn-pill btn-ghost text-[0.6rem] inline-flex items-center gap-1" data-testid={`slip-print-${o.id}`}>
                                    <Printer size={12} /> Packing slip
                                </button>
                            </span>
                        </li>
                    ))}
                </ul>
            )}
            <LeaveQuestionEditor />
        </div>
    );
}
